import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CircularProgress,
  Chip,
  Container,
  Grid,
  Stack,
  Tab,
  Typography,
  Drawer,
  useMediaQuery
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import JobFullDetails from './JobFullDetails';
import { getApplicationList } from 'api/application';
import { getJobPreferanceDetails } from 'api/users';
import { getTimeDifferenceString, changeNameFormat, formatNumber } from 'utils/Utils';
import { SplashScreen } from 'components/loading-screen';
import FilterSearch from './FilterSearch';
import EmptyState from './EmptyState';
import { useAuth } from 'hooks/useAuthContext';
import { SuitableChip, ModerateChip, NotSuitableChip } from '../icon/icon';
import JobFullDetailsMobile from './JobFullDetailsMobile';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog';


const MyJobs = ({ jobData, tab, changeTabForDownload, showExternalStatus, externalJob, onSelectTab }) => {

  const { jobApply, setJobApply } = useAuth();
  const [value, setValue] = useState(tab || 'saved-jobs');
  const [setActiveId, setSetActiveId] = useState(jobData?._id || (value === 'saved-jobs' && jobApply?.job?._id) || null);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(jobData || (value === 'saved-jobs' && jobApply?.job) || null);
  const [savedApplications, setSavedApplications] = useState([]);
  const [appliedApplications, setAppliedApplications] = useState([]);

  const [filterApplied, setFilterApplied] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);

  const [showExternalJobStatus, setShowExternalJobStatus] = useState(showExternalStatus || '');
  const [showApplication, setShowApplication] = useState([]);
  const [showExternalJob, setShowExternalJob] = useState(externalJob === 'external');
  const [isSavedPresent, setIsSavedPresent] = useState(false);
  const [isAppliedPresent, setIsAppliedPresent] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [categoryFilter, setCategory] = useState(externalJob || '');

  const cardRefs = useRef({});
  const scrollContainerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const [openDetailsDrawer, setOpenDetailsDrawer] = useState(!!jobData);

  const [jobTypes, setJobTypes] = useState([]);
  const [preferedLocation, setPreferedLocation] = useState([]);

  const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
  const dialogIds = dialogLocation.state?.dialogIds || [];
  const openDetailsDrawer = dialogIds.includes("myJobsDetailsDrawer");


  useEffect(() => {
    setLoading(true);
    setFilterApplied(!!externalJob);
    setCategory(externalJob);
    fetchData('', false, null, {}, null, null, externalJob);

    if (!!jobData && isMobile) handleOpenDetailsDrawer();
  }, []);

  useEffect(() => {
    if (setActiveId && cardRefs.current[setActiveId]) {
      scrollContainerRef.current?.scrollTo({
        top: cardRefs.current[setActiveId].offsetTop - scrollContainerRef.current.clientHeight / 2 + cardRefs.current[setActiveId].offsetHeight / 2,
        behavior: 'smooth',
      });
    }
  }, [setActiveId, loading]);

  // useEffect(() => {
  //   if (isMobile) {
  //     if (openDetailsDrawer) {
  //       localStorage.setItem('tab', '3');
  //     } else {
  //       localStorage.removeItem('tab');
  //     }
  //   }
  // }, [openDetailsDrawer, isMobile]);

  const handleOpenDetailsDrawer = () => openDialog('myJobsDetailsDrawer');
  const handleCloseDetailsDrawer = () => {
    const timer = setTimeout(() => {
      closeDialog('myJobsDetailsDrawer');
    }, 300);
  }

  /**
   * Fetches the application list based on various criteria and updates the component's state.
   *
   * @param {boolean} flag - A flag indicating whether to automatically select the first application.
   * @param {boolean} [isStatusChanged=false] - Indicates if the application status has been changed.
   * @param {string|null} [search=null] - Search term for filtering applications.
   * @param {Object} [filters={}] - Additional filters for fetching applications.
   * @param {string} sort - Sorting criteria for applications.
   * @param {string} tabs - Current tab value.
   * @param {string} category - Category filter for applications.
   * @param {boolean} [reload=false] - Flag to reload and select the first application.
   *
   * The function retrieves the list of saved and applied applications, updates the UI state accordingly,
   * maintains the selected application, and handles mobile-specific logic using local storage.
   */

  const fetchData = async (flag, isStatusChanged = false, search = null, filters = {}, sort, tabs, category, reload = false) => {
    try {
      let tabValue = tabs || value
      const response = await getApplicationList({ search, filters, sort, tab: tabs, category });
      if (response?.success && response?.data) {
        const savedApps = response.data?.savedApplication;
        const appliedApps = response.data?.nonSavedApplication;

        setIsSavedPresent(response.data?.isSavedPresent);
        setIsAppliedPresent(response.data?.isNonSavedPresent);

        setSavedApplications(savedApps);
        setAppliedApplications(appliedApps);
        setShowApplication(appliedApps);

        // if ((value === 'saved-jobs' && savedApps.length === 0) || (value === 'applied-jobs' && appliedApps.length === 0)) {
        //   setSelectedApplication(null);
        // }

        if (reload) {
          const app = value === 'saved-jobs' ? savedApps[0] || null : appliedApps?.[0] || null;
          setSelectedApplication(app);
          setSetActiveId(app?._id || null);
        }
        if (!isStatusChanged) {
          if (flag) {
            const app = (value === 'saved-jobs' && savedApps?.[0]) ? savedApps[0] : (appliedApps?.[0] || savedApps?.[0] || null);
            setSelectedApplication(app);
            setSetActiveId(app?._id || null);
            const newTabValue = (app && savedApps?.[0] === app) ? 'saved-jobs' : 'applied-jobs';
            setValue(newTabValue);
          } else if (jobData && !tabs) {
            setSelectedApplication(jobData);
            setSetActiveId(jobData?._id);
            setValue(tab);
          } else if (!selectedApplication) {
            const app = savedApps?.[0] || appliedApps?.[0];
            if (app) {
              setSelectedApplication(app);
              setSetActiveId(app._id);
              if (!tabs) {
                const newTabValue = savedApps?.length > 0 ? 'saved-jobs' : 'applied-jobs';
                setValue(newTabValue);
              }
            } else {
              setSelectedApplication(null);
              setSetActiveId(null);
            }
          }
        }

        // if (isMobile) {
        //   let localtab = localStorage.getItem('tab');
        //   let localTabValue = localStorage.getItem('tabValue');
        //   let localJobData = JSON.parse(localStorage.getItem('job'));
        //   if (localTabValue) {
        //     setValue(localTabValue);
        //     localStorage.removeItem('tabValue');
        //   }
        //   if (localJobData) {
        //     setSelectedApplication(localJobData);
        //     setSetActiveId(localJobData._id);
        //     // setOpenDetailsDrawer(true);
        //     handleOpenDetailsDrawer();
        //   }
        // }
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
      setFilterLoading(false);
    }
  };

  /**
   * Handles the tab change event and fetches the applications based on the selected tab.
   * @param {React.SyntheticEvent} event - The tab change event.
   * @param {string} newValue - The selected tab value.
   * 
   * Clears the job apply state, updates the selected tab value, and fetches the applications.
   * Also, it resets the external job status and selected application if the applied jobs tab is selected.
   * Finally, if the filter is applied or the search is open, it fetches the application data again.
   */
  const handleChange = (event, newValue) => {
    setJobApply(null);
    setValue(newValue);
    setFilterApplied(false);
    fetchData('', false, '', {}, {}, newValue);
    setShowExternalJobStatus('');
    if (showExternalJob) {
      setShowExternalJob(false);
    }
    if (newValue === 'applied-jobs') {
      setSelectedApplication(appliedApplications[0] || null);
      setSetActiveId(appliedApplications[0]?._id || null);
    } else {
      setSelectedApplication(savedApplications[0] || null);
      setSetActiveId(savedApplications[0]?._id || null);
    }
    if (filterApplied || openSearch) fetchApplicationData(newValue);
  };

  /**
   * Fetches the application data from the server and updates the component's state accordingly.
   * Called when the filter is applied or the search is opened.
   * It sets the filter loading state to true, fetches the application data, and updates the saved and applied applications, and the show application state.
   * If the response is successful, it updates the component's state with the fetched data.
   * If the response fails, it logs an error message to the console.
   * Finally, it sets the filter loading state to false, resets the filter applied state, and closes the search drawer.
   */
  const fetchApplicationData = async () => {
    try {
      setFilterLoading(true);
      const response = await getApplicationList();
      if (response?.success && response?.data) {
        setSavedApplications(response.data.savedApplication);
        setAppliedApplications(response.data.nonSavedApplication);
        setShowApplication(response.data.nonSavedApplication);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setFilterLoading(false);
      setFilterApplied(false);
      setOpenSearch(false);
    }
  };

  /**
   * Handles the selected application and fetches the applications based on the selected application.
   * Resets the job apply state and fetches the applications with the selected application.
   */
  const handleSelectedApplication = () => {
    fetchData(false, true);
  };

  /**
   * Handles the drawer open event and resets the job apply state and external job status.
   * Sets the selected application and active id to the given application if it is not null.
   * @param {boolean} newOpen - The new open state of the drawer.
   * @param {object} application - The application object to be selected.
   */
  const handleOpenDrawer = (newOpen, application) => () => {
    // setOpenDetailsDrawer(newOpen);
    newOpen ? handleOpenDetailsDrawer() : handleCloseDetailsDrawer();
    if (application) {
      setJobApply(null);
      setShowExternalJobStatus('');
      setSelectedApplication(application);
      setSetActiveId(application?._id);
    }
  };

  /**
   * Handles the application click event and resets the job apply state and external job status.
   * Sets the selected application and active id to the given application.
   * @param {object} application - The application object to be selected.
   */
  const handleApplicationClick = (application) => {
    setJobApply(null);
    setShowExternalJobStatus('');
    setSelectedApplication(application);
    setSetActiveId(application?._id);
  };

  /**
   * Handles the application click event for both mobile and desktop.
   * Sets the selected application and opens the details drawer on mobile.
   * @param {object} application - The application object to be selected.
   */
  const handleCardClick = (application) => {
    setJobApply(null);
    setShowExternalJobStatus('');
    setSelectedApplication(application);
    setSetActiveId(application?._id);
    if (isMobile) {
      openDialog('myJobsDetailsDrawer');
    }
  };

  /**
   * Handles the job application status when the user applies for a job.
   * Resets the job apply state and external job status.
   * Fetches the applications with the applied job status.
   * Closes the details drawer.
   */
  const handleAppliedJob = (msg, application) => {
    if (msg === 'delete') onSelectTab('2', application?.jobDetails);
    setShowExternalJobStatus('');
    const flag = 'applied';
    fetchData(flag);
    // setOpenDetailsDrawer(false);
    handleCloseDetailsDrawer();
    setFilterApplied(false);
  };

  const recommendedChip = (score) => score >= 3 ? <SuitableChip /> : score >= 2 ? <ModerateChip /> : <NotSuitableChip />;


  /**
   * Handles the resume download event.
   * Changes the tab to '3' and calls the changeTabForDownload function with the application data for download resume.
   * @param {object} applicationDataForDownloadResume - The application data to be downloaded.
   */
  const handleDownloadResume = (applicationDataForDownloadResume) => {
    changeTabForDownload(applicationDataForDownloadResume, '3');
  };

  /**
   * Handles the event when the user clicks on the My Jobs tab.
   * Calls the onSelectTab function with the tab index '2' as an argument.
   */
  const handleTabToMyJob = () => {
    onSelectTab('2');
  };
  const handleUpdateTjScore = (TjScore) => {
    setSavedApplications((prevJobs) =>
      prevJobs.map((job) =>
        job._id === selectedApplication?._id
          ? { ...job, tj_score: TjScore }
          : job
      )
    );
  };
  /**
   * Fetches the job preferences for the user. Updates the job types and preferred locations
   * based on the response from the server.
   * @returns {Promise} - A promise that resolves with the response from the server containing the job preferences.
   */
  const fetchJobPreferences = async () => {
    try {
      const response = await getJobPreferanceDetails();
      if (response?.success && response.data) {
        setJobTypes(response?.data?.job_type[0] || [])
        const location = response.data.prefered_location.filter((loc) => loc !== "Any");
        setPreferedLocation(location || []);
      }
      else {
        enqueueSnackbar('Failed to fetch job preferences', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch job preferences', { variant: 'error' });
    }
  };

  const showEmptyStateForCurrentTab = !loading && (
    (value === 'saved-jobs' && savedApplications.length === 0) ||
    (value === 'applied-jobs' && appliedApplications.length === 0)
  );

  const showFullWidthLayout = showEmptyStateForCurrentTab && filterApplied;

  const dataAvailableForCurrentTab = (value === 'saved-jobs' && isSavedPresent) || (value === 'applied-jobs' && isAppliedPresent);

  if (loading && !isSavedPresent && !isAppliedPresent) {
    return (
      <Grid container justifyContent="center" sx={{ position: 'relative', overflow: 'hidden', width: '100%', height: 'calc(100vh - 10vh)' }} alignItems="center">
        <SplashScreen />
      </Grid>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: { xs: 'calc(100vh - 70px)', sm: 'calc(100vh - 70px)', md: 'calc(100vh - 110px)', lg: 'calc(100vh - 110px)' },
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TabContext value={value}>
            <Box className="my-jobs-tabs">
              <Typography className="head-title" variant="h5" sx={{ display: { xs: 'block', md: 'none' }, padding: '15px', color: 'black' }}>My Jobs</Typography>
              <Container maxWidth="xl">
                <TabList onChange={handleChange} aria-label="lab API tabs example">
                  <Tab label={`Saved Jobs (${savedApplications?.length || 0})`} value="saved-jobs" />
                  <Tab label={`Applied Jobs (${showExternalJob ? showApplication?.length || 0 : appliedApplications?.length || 0})`} value="applied-jobs" />
                </TabList>
              </Container>
            </Box>
            {!showEmptyStateForCurrentTab && (
              <Box>
                <TabPanel value="saved-jobs" sx={{ padding: 0 }}>
                  <Box className="active-jobs-list">
                    {/* Filter / Search Component */}
                    <FilterSearch
                      fetchJobs={fetchData}
                      activeTab={value}
                      filterApplied={filterApplied}
                      setFilterApplied={setFilterApplied}
                      jobTypes={jobTypes}
                      preferedLocation={preferedLocation}
                      fetchJobPreferences={fetchJobPreferences}
                      loading={filterLoading}
                      setFilterLoading={setFilterLoading}
                      setOpenSearch={setOpenSearch}
                      openSearch={openSearch}
                      length={value === 'saved-jobs' ? savedApplications.length : appliedApplications.length}
                      showTitleSearchBar={isSavedPresent}
                      categoryFilter={categoryFilter}
                    />
                    <Box className="active-jobs-container" ref={scrollContainerRef} sx={{ paddingBottom: '170px!important' }}>
                      {savedApplications?.map((application, index) => (
                        <Card
                          key={application._id}
                          ref={(el) => (cardRefs.current[application._id] = el)}
                          className={`job-card-view ${(setActiveId && application._id === setActiveId) || (!setActiveId && index === 0) ? ' first-job-item' : ''}`}
                          onClick={() => { isMobile ? handleOpenDrawer(true, application)() : handleApplicationClick(application); }}
                          variant="outlined"
                          sx={{ marginBottom: '12px' }}
                          tabIndex={0}
                        >
                          <Box className="job-card-view-top">
                            <Box>{recommendedChip(application?.tj_score)}</Box>
                            <Box className="active-jobs-info">
                              {/* card content */}
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={7}>
                                  <Stack direction="row" alignItems={'center'} justifyContent={'space-between'}>
                                    <Typography className="post-title">{application?.jobDetails?.title}</Typography>
                                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" >
                                        <path d="M7.45837 3.39961L12.8917 8.83294C13.5334 9.47461 13.5334 10.5246 12.8917 11.1663L7.45837 16.5996" stroke="#6B7280" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                      </svg>
                                    </Box>
                                  </Stack>
                                  {application?.jobDetails?.is_company_name_confidential ? <Typography className="company-name">Company Name - Confidential</Typography> : <Typography className="company-name">{application?.jobDetails?.company || ''}</Typography>}
                                  <Box mt={0.5} className="work-type">
                                    <Stack direction="row" flexWrap="wrap" alignItems={'center'} spacing={0.5}>
                                      <Typography variant="p" className="city">{application?.jobDetails?.location}</Typography>
                                      {application?.jobDetails?.job_type === 'Full time' && <>
                                        <Typography variant="body1">•</Typography>
                                        <Typography variant="p" className="city">{application?.jobDetails?.job_type}</Typography>
                                      </>}
                                      <Typography variant="body1">•</Typography>
                                      <Typography variant="p" className="city">{application?.jobDetails?.work_mode}</Typography>
                                    </Stack>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={5}>
                                  {application?.jobDetails?.is_compensation_confidential || !application?.jobDetails?.salary ?
                                    <Box className="ctc-range">
                                      {/* <Box className="hide-my-info"></Box> */}
                                      <Typography className="text-1" >Not Disclosed</Typography>
                                      <Typography className="text-2">Compensation</Typography>
                                    </Box> :
                                    <Box className="ctc-range">
                                      <Typography className="text-1">
                                        ₹{application?.jobDetails?.job_type === 'Full time' ? formatNumber(application?.jobDetails?.salary) : Intl.NumberFormat('en-IN').format(application?.jobDetails?.salary)}
                                      </Typography>
                                      <Typography className="text-2">{application?.jobDetails?.job_type === 'Full time' ? application?.jobDetails?.compensation : application?.jobDetails?.compensation_model}</Typography>
                                    </Box>
                                  }
                                </Grid>
                              </Grid>
                              {/* card bottom */}
                              <Box sx={{ marginTop: { xs: '12px', md: '8px' } }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent={'space-between'} spacing={{ xs: 2, md: 0 }}>
                                  <Typography variant="p" className="posted-date">
                                    {application?.jobDetails?.updatedAt
                                      ? getTimeDifferenceString(application?.jobDetails?.updatedAt, "Posted")
                                      : 'NA'}
                                  </Typography>
                                </Stack>
                              </Box>
                            </Box>
                          </Box>
                          {/* Part time band */}
                          {application?.jobDetails?.job_type !== 'Full time' &&
                            <Box className="job-card-view-bottom">
                              <Box mt={0.5} className="work-type">
                                <Stack direction="row" flexWrap="wrap" alignItems={'center'} spacing={0.5}>
                                  <Typography variant="p" className="city">{application?.jobDetails?.job_type}</Typography>
                                  <Typography variant="body1">•</Typography>
                                  <Typography variant="p">{application?.jobDetails?.weekly_time_commitment} hrs./week</Typography>
                                  <Typography variant="body1">•</Typography>
                                  <Typography variant="p">For {application?.jobDetails?.total_time_commitment_requirement} weeks</Typography>
                                </Stack>
                              </Box>
                            </Box>}
                        </Card>
                      ))}
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel value="applied-jobs" sx={{ padding: 0 }}>
                  <Box className="active-jobs-list">
                    {/* Filter / Search Component */}
                    <FilterSearch
                      preferedLocation={preferedLocation}
                      jobTypes={jobTypes}
                      fetchJobPreferences={fetchJobPreferences}
                      fetchJobs={fetchData}
                      activeTab={value}
                      loading={filterLoading}
                      setFilterLoading={setFilterLoading}
                      filterApplied={filterApplied}
                      setFilterApplied={setFilterApplied}
                      setOpenSearch={setOpenSearch}
                      openSearch={openSearch}
                      showTitleSearchBar={isAppliedPresent}
                      length={appliedApplications?.length}
                    />
                    <Box className="active-jobs-container" ref={scrollContainerRef} sx={{ paddingBottom: '170px!important' }}>
                      {showApplication?.map((application, index) => (
                        <Card
                          key={application._id}
                          ref={(el) => (cardRefs.current[application._id] = el)}
                          className={`job-card-view ${(setActiveId && application._id === setActiveId) || (!setActiveId && index === 0) ? ' first-job-item' : ''}`}
                          onClick={() => { isMobile ? handleOpenDrawer(true, application)() : handleApplicationClick(application); }}
                          variant="outlined"
                          sx={{ marginBottom: '12px' }}
                          tabIndex={0}
                        >
                          <Box className="job-card-view-top">
                            <Box>
                              <Box mb={1}>{recommendedChip(application?.tj_score)}</Box>
                              <Chip
                                className={`job-${application.status === 'not selected' ? "rejected" : "applied"}`}
                                label={changeNameFormat(application.status)}
                                size="small"
                              />
                            </Box>
                            <Box className="active-jobs-info">
                              {/* card content */}
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={7}>
                                  <Stack direction="row" alignItems={'center'} justifyContent={'space-between'}>
                                    <Typography className="post-title">{application?.jobDetails?.title}</Typography>
                                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" >
                                        <path d="M7.45837 3.39961L12.8917 8.83294C13.5334 9.47461 13.5334 10.5246 12.8917 11.1663L7.45837 16.5996" stroke="#6B7280" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                      </svg>
                                    </Box>
                                  </Stack>
                                  {application?.jobDetails?.is_company_name_confidential ? <Typography className="company-name">Company Name - Confidential</Typography> : <Typography className="company-name">{application?.jobDetails?.company || ''}</Typography>}
                                  <Box mt={0.5} className="work-type">
                                    <Stack direction="row" flexWrap="wrap" alignItems={'center'} spacing={0.5}>
                                      <Typography variant="p" className="city">{application?.jobDetails?.location}</Typography>
                                      {application?.jobDetails?.job_type === 'Full time' && <>
                                        <Typography variant="body1">•</Typography>
                                        <Typography variant="p" className="city">{application?.jobDetails?.job_type}</Typography>
                                      </>}
                                      <Typography variant="body1">•</Typography>
                                      <Typography variant="p" className="city">{application?.jobDetails?.work_mode}</Typography>
                                    </Stack>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={5}>
                                  {application?.jobDetails?.is_compensation_confidential || !application?.jobDetails?.salary ?
                                    <Box className="ctc-range">
                                      <Typography className="text-1" >Not Disclosed</Typography>
                                      <Typography className="text-2">Compensation</Typography>
                                    </Box> :
                                    <Box className="ctc-range">
                                      <Typography className="text-1">
                                        ₹{application?.jobDetails?.job_type === 'Full time' ? formatNumber(application?.jobDetails?.salary) : Intl.NumberFormat('en-IN').format(application?.jobDetails?.salary)}
                                      </Typography>
                                      <Typography className="text-2">{application?.jobDetails?.job_type === 'Full time' ? application?.jobDetails?.compensation : application?.jobDetails?.compensation_model}</Typography>
                                    </Box>
                                  }
                                </Grid>
                              </Grid>
                              {/* card bottom */}
                              <Box sx={{ marginTop: { xs: '12px', md: '8px' } }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent={'space-between'} spacing={{ xs: 2, md: 0 }}>
                                  <Typography variant="p" className="posted-date">
                                    {application?.applied_at
                                      ? 'Applied on ' + dayjs(application?.applied_at).format('DD MMM, YYYY') //getTimeDifferenceString(application?.applied_at, 'Applied')
                                      : 'N/A'}
                                  </Typography>
                                </Stack>
                              </Box>
                            </Box>
                          </Box>
                          {/* Part time band */}
                          {application?.jobDetails?.job_type !== 'Full time' &&
                            <Box className="job-card-view-bottom">
                              <Box mt={0.5} className="work-type">
                                <Stack direction="row" flexWrap="wrap" alignItems={'center'} spacing={0.5}>
                                  <Typography variant="p" className="city">{application?.jobDetails?.job_type}</Typography>
                                  <Typography variant="body1">•</Typography>
                                  <Typography variant="p">{application?.jobDetails?.weekly_time_commitment} hrs./week</Typography>
                                  <Typography variant="body1">•</Typography>
                                  <Typography variant="p">For {application?.jobDetails?.total_time_commitment_requirement} weeks</Typography>
                                </Stack>
                              </Box>
                            </Box>}
                        </Card>
                      ))}
                    </Box>
                  </Box>
                </TabPanel>
              </Box>
            )}
          </TabContext>
        </Grid>
        <Grid item xs={12} md={8}>
          {/* job full details */}
          {!showEmptyStateForCurrentTab && selectedApplication && (
            isMobile ?
              <Drawer
                className="mob-side-panel"
                open={openDetailsDrawer}
                anchor={'right'}
                onClose={handleOpenDrawer(false, null)}>
                <JobFullDetailsMobile
                  tab={value}
                  application={selectedApplication}
                  fetchJobPreferences={fetchJobPreferences}
                  onStatusChange={handleSelectedApplication}
                  onApplied={handleAppliedJob}
                  downloadResume={handleDownloadResume}
                  showExternalJobStatus={showExternalJobStatus}
                  updateTJScore={handleUpdateTjScore}
                  onClose={handleOpenDrawer(false, null)} />
              </Drawer> :
              <JobFullDetails
                tab={value}
                fetchJobPreferences={fetchJobPreferences}
                application={selectedApplication}
                onStatusChange={handleSelectedApplication}
                onApplied={handleAppliedJob}
                downloadResume={handleDownloadResume}
                showExternalJobStatus={showExternalJobStatus}
                updateTJScore={handleUpdateTjScore}
              />
          )}
        </Grid>

        <Grid item xs={12}>
          {showEmptyStateForCurrentTab && !dataAvailableForCurrentTab && <EmptyState tab={value} onChangeTab={onSelectTab} />}
          {/* <EmptyState tab={value} onChangeTab={onSelectTab} /> */}
        </Grid>
      </Grid>
      <Drawer open={filterLoading}>
        <div className="loading-overlay">
          <div className="loading-spinner">
            <CircularProgress />
          </div>
        </div>
      </Drawer>
    </Box >
  );
};

export default MyJobs;